import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const L = {
  valid: { en: "VALID", cs: "PLATNÁ", he: "בתוקף", ru: "ДЕЙСТВИТЕЛЬНА" },
  revoked: { en: "NOT VALID", cs: "NEPLATNÁ", he: "לא בתוקף", ru: "НЕДЕЙСТВИТЕЛЬНА" },
  pending: { en: "AWAITING APPROVAL", cs: "ČEKÁ NA SCHVÁLENÍ", he: "ממתין לאישור", ru: "ОЖИДАЕТ ОДОБРЕНИЯ" },
  notfound: { en: "This card does not exist.", cs: "Tato karta neexistuje.", he: "הכרטיס לא נמצא.", ru: "Карта не найдена." },
  issued: { en: "Issued", cs: "Vydána", he: "הוצא", ru: "Выдана" },
  show: { en: "Show this screen at the till", cs: "Ukažte tuto obrazovku u pokladny", he: "הציגו מסך זה בקופה", ru: "Покажите этот экран на кассе" },
};

export default function StudentCardPage() {
  const { code } = useParams();
  const { lang } = useLang();
  const tx = (k) => L[k][lang] || L[k].en;
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { data: card, isError, isLoading } = useQuery({ queryKey: ["card", code], queryFn: async () => (await api.get(`/card/${code}`)).data, retry: false });

  const ok = card?.status === "approved";
  const state = card ? (ok ? "valid" : card.status === "revoked" ? "revoked" : "pending") : null;
  const stateCls = { valid: "bg-[#2f9e5b] text-white", revoked: "bg-red-600 text-white", pending: "bg-[#BE923C] text-white" };
  const StateIcon = { valid: CheckCircle2, revoked: XCircle, pending: Clock }[state] || Clock;

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-5" data-testid="student-card-page" dir={lang === "he" ? "rtl" : "ltr"}>
      {isLoading && <p className="text-white/60">…</p>}
      {isError && <p className="text-white/80" data-testid="student-card-notfound">{tx("notfound")}</p>}
      {card && (
        <motion.div initial={{ opacity: 0, y: 20, rotateX: 8 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.6 }}
          className={`w-full max-w-sm rounded-[28px] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)] ${ok ? "" : "grayscale-[60%]"}`} data-testid="student-card">
          <div className={`flex items-center justify-center gap-2 py-3 text-sm font-bold tracking-[0.2em] ${stateCls[state]}`} data-testid="student-card-status">
            <StateIcon size={18} /> {tx(state)}
          </div>
          <div className="bg-[#F9F8F6] p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#BE923C]">Chabad of Prague · Students Club</p>
            <div className="mt-5 flex items-start gap-4">
              {card.photo_url && <img src={resolveImg(card.photo_url)} alt="" className="w-24 h-24 rounded-2xl object-cover border-2 border-[#BE923C] shrink-0" data-testid="student-card-photo" />}
              <div className="min-w-0">
                <h1 className="font-serif text-2xl text-[#0F172A] leading-tight break-words" data-testid="student-card-name">{card.name}</h1>
                <p className="mt-1 text-sm text-[#475569]">{[card.university, card.faculty, card.year].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
            <div className="mt-6 rounded-2xl bg-[#0F172A] text-white p-4">
              <p className="font-semibold text-[#D8B45E] leading-snug" data-testid="student-card-discount">{card.discount_text}</p>
            </div>
            <p className="mt-5 text-xs text-[#475569]">{tx("issued")}: {card.issued}</p>
          </div>
          <div className="bg-[#E8E6E1] text-center text-xs text-[#475569] py-2">{tx("show")}</div>
        </motion.div>
      )}
    </div>
  );
}
